import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider, createBrowserRouter } from "react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { retryUnlessTerminal } from "@/api/client";
import { App } from "@/App";
import { AuthProvider } from "@/auth/AuthProvider";
import { RouteError } from "@/components/RouteError";
import { loadRuntimeConfig } from "@/runtimeConfig";
import "@/styles/index.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: retryUnlessTerminal,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

async function bootstrap() {
  await loadRuntimeConfig();
  // A DATA router, with one splat route that renders the whole app: the router's blocker (what holds
  // a navigation while a form has unsaved changes, see ``hooks/useUnsavedChanges``) only exists in
  // one — ``<BrowserRouter>`` cannot block. The ``<Routes>`` of ``App`` keep working unchanged as
  // descendants of the splat route, and there are no loaders, actions or fetchers: the data router
  // is used for its history and its blockers, not for its data APIs. React Router 7 resolves a
  // relative path from the full URL wherever the deepest route with a path is the splat — the shell
  // (sidebar, tree, top bar), the ``*`` fallbacks and the error element — instead of from "/". Pages
  // under their own path are unaffected. Every link and navigation of the console is absolute; keep
  // them so.
  const router = createBrowserRouter([{ path: "*", element: <App />, errorElement: <RouteError /> }]);
  // The auth and query providers do not need the router, so they sit above it.
  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <AuthProvider>
        <QueryClientProvider client={queryClient}>
          {/* No transitions: React Router 7 renders each navigation in ``startTransition`` by default,
              and a field that writes the URL on every key (the Tenant filter of Emails) then lost
              keys and its caret. It also left the exit dialog clickable a second time after the
              router had moved on. This is how React Router 6 rendered. */}
          <RouterProvider router={router} useTransitions={false} />
        </QueryClientProvider>
      </AuthProvider>
    </StrictMode>,
  );
}

bootstrap().catch((err) => {
  console.error("Bootstrap failed", err);
  const root = document.getElementById("root");
  if (!root) return;
  // Build the error UI with DOM APIs so a stringified error containing HTML
  // (e.g. from a fetch response leaked into the message) can't execute. The
  // previous ``innerHTML`` interpolation was a small XSS smell (review L8).
  root.replaceChildren();
  const wrap = document.createElement("div");
  wrap.style.padding = "2rem";
  wrap.style.fontFamily = "system-ui";
  const title = document.createElement("h1");
  title.textContent = "Control plane failed to start";
  const detail = document.createElement("p");
  detail.textContent = String(err);
  wrap.append(title, detail);
  root.append(wrap);
});
